/**
 * The `sections` landing page — one machine room's place on the Survey Map,
 * on paper (D-028).
 *
 * This route used to be the elevator hall indicator: the only cross-section
 * navigation the app had, one room at a time, with a wizard sentence and an
 * instruction Notice above the rows. The map bar and sheet are that
 * navigation now, everywhere, so this screen keeps one job: a landing page
 * for any link that still points at a room — the building screen's room
 * slots, the close screen's still-to-walk rows — rendering the SAME tree the
 * sheet renders, with this room's branch open. One renderer, read twice;
 * nothing here forms its own opinion of a lamp.
 *
 * Rows are anchors, as before: long-press, open-in-new-tab and a screen
 * reader's link list all work on the app's primary navigation.
 */

import { useState, type ReactNode } from 'react';

import { MapTreeView } from '../components';
import { hrefFor } from '../routing';
import { useRoute } from '../useRoute';
import { mapModel, ROOM_TYPE_LABEL } from '../state/mapTree';
import { useSurvey } from '../state/SurveyProvider';
import { summarize } from '../state/surveyList';
import { useWalkedSections } from '../state/walked';

export interface SectionsScreenProps {
  surveyId: string;
  roomId: string;
}

export function SectionsScreen({ surveyId, roomId }: SectionsScreenProps): ReactNode {
  const { record, doc, results, room } = useSurvey();
  const route = useRoute();
  const walked = useWalkedSections(surveyId);
  const machineRoom = room(roomId);
  // This room open, the others folded — the page is about this room.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set([roomId]));

  if (!machineRoom) {
    return (
      <>
        <p className="empty">That machine room isn&rsquo;t set up yet.</p>
        <a className="btn btn--ghost" href={hrefFor({ name: 'building', surveyId })}>
          Back to building
        </a>
      </>
    );
  }

  const model = mapModel({
    doc,
    results,
    walked,
    surveyTitle: summarize(record).title,
    route,
  });

  return (
    <>
      <h2 className="eyebrow" data-legibility="display">
        {machineRoom.name} · {ROOM_TYPE_LABEL[machineRoom.type] ?? machineRoom.type}
      </h2>
      <MapTreeView
        model={model}
        openBranches={open}
        onToggleBranch={(id) => {
          setOpen((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }}
        className="maptree--page"
      />
    </>
  );
}
